import {
    Box,
    FormLabel,
    Switch,
    Text,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import PageCard from "../../../components/page-card";
import { translate } from "../../../i18n";
import { useSelectedOrganization } from "../hooks/useSelectedOrganization";
import { useUpdateOrganizationCoachingPayLaterEnabled } from "../hooks/useUpdateOrganizationCoachingPayLaterEnabled";

// Per-tenant feature gate for the coaching "Pay later" option. Operators flip
// this from the dashboard; the value is read by the mobile client to decide
// whether to show the pay-later choice in the coach booking sheet, and is also
// enforced server-side on the pay-later coaching endpoint. The toggle is the
// source of truth — no Save button, the mutation fires on each flip and toasts
// on resolution.
export default function CoachingPayLaterSettingsCard() {
    const { selectedOrganization } = useSelectedOrganization();
    const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();

    // Default to false so old API responses without the field don't render
    // the switch in an undefined-checked state. The API treats absence as
    // false too, so this stays consistent across the wire.
    const enabled = selectedOrganization?.coachingPayLaterEnabled ?? false;

    const { mutate, isLoading } = useUpdateOrganizationCoachingPayLaterEnabled(
        selectedOrganization?.id || '',
        () => {
            toast({
                title: translate('organization.coaching_pay_later_saved'),
                status: 'success',
                duration: 2000,
                position: 'top-right',
            });
        },
    );

    const handleToggle = (nextEnabled: boolean) => {
        if (!selectedOrganization) return;
        if (isLoading) return; // guard against double-flips while in flight
        mutate(
            { coachingPayLaterEnabled: nextEnabled },
            {
                onError: () => {
                    toast({
                        title: translate('error'),
                        status: 'error',
                        duration: 3000,
                        position: 'top-right',
                    });
                },
            },
        );
    };

    if (!selectedOrganization) return null;

    return (
        <PageCard title={translate('organization.coaching_pay_later_title')}>
            <Box p={4}>
                <Box display="flex" alignItems="center" gap={3} mb={2}>
                    <Switch
                        isChecked={enabled}
                        isDisabled={isLoading}
                        onChange={(e) => handleToggle(e.target.checked)}
                        colorScheme="brand"
                        aria-label={translate('organization.coaching_pay_later_title')}
                    />
                    <FormLabel mb={0} color={textColorPrimary}>
                        {translate('organization.coaching_pay_later_title')}
                    </FormLabel>
                </Box>
                <Text fontSize="sm" color="gray.500">
                    {translate('organization.coaching_pay_later_helper')}
                </Text>
            </Box>
        </PageCard>
    );
}
